import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import { stepType } from 'utils/reactFlowUtils';

const selectedRangeInit = Object.freeze({
  cycles: -1,
  averageProcessTime: -1,
  avgGap: -1,
  estimate: -1,
  yieldRate: -1,
});

const SelectionContext = createContext(undefined);

const SET_SELECT_ALL = 'SET_SELECT_ALL';
const SET_ACTIVE_NODE = 'SET_ACTIVE_NODE';
const SET_SELECTED_RANGE = 'SET_SELECTED_RANGE';

function selectionReducer(currentState, action) {
  switch (action.type) {
    case SET_SELECT_ALL:
      return { ...currentState, selectAll: action.payload };
    case SET_ACTIVE_NODE:
      return {
        ...currentState,
        selectAll: false,
        activeNode: action.payload,
        selectedRange: selectedRangeInit,
      };
    case SET_SELECTED_RANGE:
      return { ...currentState, selectedRange: action.payload, selectAll: false };
    default:
      return currentState;
  }
}

export default function SelectionProvider({ root, children }) {
  const [state, dispatch] = useReducer(selectionReducer, {
    selectAll: false,
    activeNode: root.uniqueId,
    selectedRange: selectedRangeInit,
  });

  const setSelectAll = useCallback((selectAll) => {
    dispatch({ type: SET_SELECT_ALL, payload: selectAll });
  }, []);

  const setActiveNode = useCallback((activeNodeId) => {
    dispatch({ type: SET_ACTIVE_NODE, payload: activeNodeId });
  }, []);

  const setSelectedRange = useCallback((selectedRange) => {
    dispatch({ type: SET_SELECTED_RANGE, payload: selectedRange });
  }, []);

  const value = useMemo(() => ({
    ...state,
    setSelectAll,
    setActiveNode,
    setSelectedRange,
  }), [state, setSelectAll, setActiveNode, setSelectedRange]);

  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  );
}

SelectionProvider.propTypes = {
  root: stepType.isRequired,
  children: PropTypes.node.isRequired,
};

export function useSelection() {
  const context = useContext(SelectionContext);
  if (context === undefined) {
    throw new Error('useSelection must be used within a SelectionProvider');
  }
  return context;
}
